import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import type {
  CallingRuleAction,
  CallingRuleDoc,
  CallingRuleFormData,
  CallingRuleState,
  CallingRuleTrigger,
} from "../../callingConfiguration/callingRuleUtils";
import {
  buildCallingConditionConfigFromCmmColumns,
  CALLING_CONDITION_FIELD_OPTIONS,
  mapRuleStatesToFirestorePayload,
} from "../../callingConfiguration/callingRuleUtils";
import type { FirstStpConfigBlock } from "./firstStpLogicUtils";
import {
  buildFirstStpReviewConditionBlocksPayload,
  parseFirstStpReviewDoc,
  type FirstStpConditionBlockLayout,
  type FirstStpReviewFirestoreDoc,
} from "./firstStpConditionBlockDocUtils";

export const FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS =
  CALLING_CONDITION_FIELD_OPTIONS;

export type FirstStpReviewRuleAction = CallingRuleAction;
export type FirstStpReviewRuleTrigger = CallingRuleTrigger;
export type FirstStpReviewRuleDoc = FirstStpReviewFirestoreDoc;
export type FirstStpReviewRuleState = CallingRuleState;
export type FirstStpReviewRuleFormData = CallingRuleFormData;

export const DEFAULT_FIRST_STP_REVIEW_DOC_ID = "first_stp_review";

export const DEFAULT_FIRST_STP_DOC_TRIGGERS: CallingRuleTrigger[] = [
  { type: "before_stp" },
];

export const FIRST_STP_REVIEW_CONDITION_TITLE = "First STP review condition";

export { buildCallingConditionConfigFromCmmColumns as buildFirstStpReviewConditionConfigFromCmmColumns };

export {
  groupRulesByConditionBlockLayout,
  parseFirstStpReviewDoc,
  type FirstStpConditionBlockLayout,
} from "./firstStpConditionBlockDocUtils";

/** Maps a Firestore first STP review doc to editable UI rule states. */
export function mapFirstStpReviewDocToRuleStates(
  doc: FirstStpReviewRuleDoc,
): FirstStpReviewRuleState[] {
  return parseFirstStpReviewDoc(doc).rules;
}

/** Resolve document-level triggers for first STP review configs. */
export function resolveFirstStpDocTriggers(
  doc: FirstStpReviewRuleDoc,
): FirstStpReviewRuleTrigger[] {
  if (doc.triggers?.length) {
    return doc.triggers.map((t) => ({ ...t }));
  }
  for (const rule of doc.rules ?? []) {
    if (rule.triggers?.length) {
      return rule.triggers.map((t) => ({ ...t }));
    }
  }
  return DEFAULT_FIRST_STP_DOC_TRIGGERS.map((t) => ({ ...t }));
}

/**
 * Builds the Firestore payload for first STP review (condition_blocks or flat rules).
 *
 * @input rules - Current UI rules
 * @input triggers - Document triggers
 * @input existingDoc - Loaded Firestore document
 * @input updatedBy - User email
 * @input blockLayout - Block layout when doc uses condition_blocks
 * @input usesConditionBlocks - Whether to write condition_blocks format
 * @return FirstStpReviewRuleDoc - Payload for setDocument
 */
export function mapFirstStpReviewRuleStatesToFirestorePayload(
  rules: FirstStpReviewRuleState[],
  triggers: FirstStpReviewRuleTrigger[],
  existingDoc: FirstStpReviewRuleDoc | null,
  updatedBy?: string | null,
  blockLayout: FirstStpConditionBlockLayout[] = [],
  usesConditionBlocks = false,
): FirstStpReviewRuleDoc {
  if (usesConditionBlocks) {
    return buildFirstStpReviewConditionBlocksPayload(
      rules,
      blockLayout,
      triggers,
      existingDoc,
      updatedBy,
    );
  }
  return mapRuleStatesToFirestorePayload(
    rules,
    triggers,
    existingDoc as CallingRuleDoc | null,
    updatedBy,
  );
}

/** Default empty rule for first STP review (needs_review set via actions). */
export function createEmptyFirstStpReviewRule(
  index: number,
): FirstStpReviewRuleState {
  return {
    id: `first-stp-review-rule-${uuidv4()}`,
    label: `Rule ${index + 1}`,
    subtext: "",
    active: true,
    actions: [
      { type: "set", field: "needs_review", value: "Needed" },
      { type: "set", field: "review_reason", value: "" },
    ],
    condition: createEmptyGroup(),
    conditionKey: "rule",
  };
}

/** Seed state when the Firestore document has no rules yet. */
export const getDefaultFirstStpReviewRules = (): FirstStpReviewRuleState[] => [
  createEmptyFirstStpReviewRule(0),
];

/** Default block layout for a new empty condition_blocks document. */
export const getDefaultFirstStpBlockLayout = (
  rules: FirstStpReviewRuleState[],
): FirstStpConditionBlockLayout[] => [
  {
    lable: "Condition 1",
    subtext: "",
    active: true,
    ruleIds: rules.map((rule) => rule.id),
  },
];

/**
 * Creates an empty condition block layout entry for a new block at the given index.
 *
 * @input index - Zero-based index of the new block
 * @return FirstStpConditionBlockLayout - Empty block with no rules yet
 */
export const createEmptyConditionBlockLayout = (
  index: number,
): FirstStpConditionBlockLayout => ({
  lable: `Condition ${index + 1}`,
  subtext: "",
  active: true,
  ruleIds: [],
});
